import React, { useState } from "react";
import axios from "axios";
import { Fuel, Mail, Lock, User, Eye, EyeOff, ArrowRight, ArrowLeft, KeyRound } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage({ onLogin }) {
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset flow state
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "register") {
        if (!name.trim()) {
          toast.error("Please enter your name");
          setLoading(false);
          return;
        }
        const resp = await axios.post(`${API}/api/auth/register`, {
          email: email.trim(),
          password,
          name: name.trim(),
        }, {});
        onLogin(resp.data);
        toast.success("Account created!");
      } else {
        const resp = await axios.post(`${API}/api/auth/login`, {
          email: email.trim(),
          password,
        }, {});
        onLogin(resp.data);
        toast.success(`Welcome back, ${resp.data.name}!`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const cleaned = email.trim();
    if (!cleaned) {
      toast.error("Please enter your email address");
      return;
    }
    // Lightweight client-side format check so we don't bother the server with obvious junk.
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleaned)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/api/auth/forgot-password`, {
        email: cleaned,
      });
      if (resp.data.reset_code) {
        setGeneratedCode(resp.data.reset_code);
      }
      toast.success("Reset code sent — check your inbox");
      setMode("reset");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Pydantic validation returns { detail: [{msg, loc, ...}] }
      let msg;
      if (Array.isArray(detail)) {
        msg = "Please enter a valid email address";
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (status === 502 || status === 504) {
        // Cloudflare / ingress 5xx HTML pages have no usable detail field.
        msg = "Email service is slow or unavailable. Please try again in a minute.";
      } else if (status === 429) {
        msg = "Too many attempts. Please wait a moment before trying again.";
      } else if (!err?.response) {
        msg = "Network error — please check your connection and try again.";
      } else {
        msg = `Couldn't send reset code (HTTP ${status || "?"}). Please try again shortly.`;
      }
      if (status === 400 && msg.toLowerCase().includes("google")) {
        // Account uses Google sign-in — guide user back to the right button
        toast.error(msg, { duration: 7000 });
        setMode("login");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetCode.trim()) {
      toast.error("Please enter the reset code");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/api/auth/reset-password`, {
        email: email.trim(),
        code: resetCode.trim(),
        new_password: newPassword,
      });
      toast.success(resp.data.message);
      setMode("login");
      setPassword("");
      setResetCode("");
      setNewPassword("");
      setGeneratedCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const goBackToLogin = () => {
    setMode("login");
    setResetCode("");
    setNewPassword("");
    setGeneratedCode("");
  };

  const inputClass = "w-full bg-transparent border-none outline-none text-[#1A1A1A] text-sm placeholder-[#999999] font-['Manrope']";
  const fieldClass = "flex items-center gap-3 bg-[#FAFAF7] border border-[#EBE6DC] rounded-xl px-4 py-3.5 transition-all focus-within:border-[#E52020] focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(229,32,32,0.08)]";

  return (
    <div className="min-h-screen flex bg-[#F7F5F0]">
      {/* Left: Cinematic image (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1773609689026-062b9216661a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwY2FyJTIwZHJpdmluZyUyMHN1bm55JTIwbW9kZXJufGVufDB8fHx8MTc3ODM4NzQ3Mnww&ixlib=rb-4.1.0&q=85')"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/10 to-[#F7F5F0]/30" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center">
              <Fuel className="w-4 h-4" />
            </div>
            <span className="text-sm font-bold tracking-[0.28em] uppercase">DRIVE<span className="text-[#FF6B6B] font-mono">$</span>PEND</span>
          </div>
          <div>
            <h2 className="text-5xl font-extralight tracking-tighter leading-tight mb-4">
              Every kilometre,<br />
              <span className="font-medium">precisely calculated.</span>
            </h2>
            <p className="text-white/85 text-sm max-w-md font-light leading-relaxed">
              Live NZ fuel prices from Gaspy, route-aware tolls, RUC and ferry costs — all in one premium calculator.
            </p>
          </div>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="lg:hidden w-14 h-14 rounded-2xl bg-white border border-[#EBE6DC] shadow-sm flex items-center justify-center mx-auto mb-4">
              <Fuel className="w-7 h-7 text-[#E52020]" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-extralight text-[#1A1A1A] tracking-tighter mb-2" data-testid="login-page-title">DRIVE<span className="text-[#E52020] font-medium" style={{fontFamily:"'IBM Plex Mono',monospace"}}>$</span>PEND</h1>
            <p className="text-xs text-[#999999] mt-3 font-bold tracking-[0.22em] uppercase">
              {mode === "login" && "Sign in to your account"}
              {mode === "register" && "Create your account"}
              {mode === "forgot" && "Reset your password"}
              {mode === "reset" && "Enter reset code"}
            </p>
          </div>

          {/* Forgot Password */}
          {mode === "forgot" && (
            <>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-[#666666] mb-4 font-['Manrope']">Enter your email and we'll generate a reset code.</p>
                <div className={fieldClass}>
                  <Mail className="w-4 h-4 text-[#E52020] flex-shrink-0" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className={inputClass} required data-testid="forgot-email-input" />
                </div>
                <button type="submit" disabled={loading} className="w-full py-3 bg-[#E52020] text-white font-bold rounded-full flex items-center justify-center gap-2 hover:bg-[#CC1B1B] hover:shadow-[0_8px_24px_rgba(229,32,32,0.34)] transition-all tracking-[0.14em] uppercase disabled:opacity-30" data-testid="forgot-submit-btn">
                  {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : (<>SEND RESET CODE<ArrowRight className="w-4 h-4" /></>)}
                </button>
              </form>
              <button onClick={goBackToLogin} className="flex items-center gap-2 text-sm text-[#666666] mt-4 hover:text-[#E52020] transition-colors font-['Manrope']" data-testid="back-to-login-btn">
                <ArrowLeft className="w-3.5 h-3.5" />Back to sign in
              </button>
            </>
          )}

          {/* Reset Password */}
          {mode === "reset" && (
            <>
              <form onSubmit={handleResetPassword} className="space-y-4">
                {generatedCode && (
                  <div className="flex items-center gap-3 p-4 bg-[rgba(229, 32, 32,0.04)] border border-[rgba(229, 32, 32,0.15)] rounded-sm" data-testid="reset-code-display">
                    <KeyRound className="w-5 h-5 text-[#E52020]" />
                    <div>
                      <p className="text-xs text-[#666666] font-['Manrope']">YOUR RESET CODE</p>
                      <p className="text-xl font-bold text-[#E52020] tracking-[0.3em] font-mono">{generatedCode}</p>
                    </div>
                  </div>
                )}
                <div className={fieldClass}>
                  <KeyRound className="w-4 h-4 text-[#E52020] flex-shrink-0" />
                  <input type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value)} placeholder="6-digit reset code" className={inputClass} maxLength={6} required data-testid="reset-code-input" />
                </div>
                <div className={fieldClass}>
                  <Lock className="w-4 h-4 text-[#E52020] flex-shrink-0" />
                  <input type={showPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (min 6 chars)" className={inputClass} required minLength={6} data-testid="new-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[#666666] hover:text-[#E52020] transition-colors">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button type="submit" disabled={loading} className="w-full py-3 bg-[#E52020] text-white font-bold rounded-full flex items-center justify-center gap-2 hover:bg-[#CC1B1B] hover:shadow-[0_8px_24px_rgba(229,32,32,0.34)] transition-all tracking-[0.14em] uppercase disabled:opacity-30" data-testid="reset-submit-btn">
                  {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : (<>RESET PASSWORD<ArrowRight className="w-4 h-4" /></>)}
                </button>
              </form>
              <button onClick={goBackToLogin} className="flex items-center gap-2 text-sm text-[#666666] mt-4 hover:text-[#E52020] transition-colors font-['Manrope']" data-testid="back-to-login-btn">
                <ArrowLeft className="w-3.5 h-3.5" />Back to sign in
              </button>
            </>
          )}

          {/* Login / Register */}
          {(mode === "login" || mode === "register") && (
            <>
              {/* Apple Guideline 4.8: hide third-party login in the iOS native shell so the app
                  doesn't need to also offer Sign in with Apple. Email/password remains available
                  for everyone. The Google button still renders on the web and on Android. */}
              {!(typeof window !== "undefined" && window.__IS_IOS_NATIVE) && (
                <>
                  <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 py-3 bg-[rgba(229, 32, 32,0.04)] border border-[rgba(229, 32, 32,0.15)] rounded-sm text-sm text-[#1A1A1A] hover:bg-[rgba(229, 32, 32,0.08)] hover:border-[rgba(229, 32, 32,0.3)] transition-all font-['Manrope']" data-testid="google-login-btn">
                    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 3.58z" fill="#EA4335"/>
                    </svg>
                    Continue with Google
                  </button>

                  <div className="flex items-center gap-3 my-6">
                    <div className="flex-1 h-px bg-[rgba(229, 32, 32,0.1)]" />
                    <span className="text-xs text-[#475569] font-['Manrope'] tracking-wider">OR</span>
                    <div className="flex-1 h-px bg-[rgba(229, 32, 32,0.1)]" />
                  </div>
                </>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === "register" && (
                  <div className={fieldClass}>
                    <User className="w-4 h-4 text-[#E52020] flex-shrink-0" />
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputClass} data-testid="register-name-input" />
                  </div>
                )}
                <div className={fieldClass}>
                  <Mail className="w-4 h-4 text-[#E52020] flex-shrink-0" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className={inputClass} required data-testid="email-input" />
                </div>
                <div className={fieldClass}>
                  <Lock className="w-4 h-4 text-[#E52020] flex-shrink-0" />
                  <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputClass} required minLength={6} data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[#666666] hover:text-[#E52020] transition-colors">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {mode === "login" && (
                  <button type="button" onClick={() => setMode("forgot")} className="text-xs text-[#E52020] hover:underline font-['Manrope']" data-testid="forgot-password-btn">
                    Forgot password?
                  </button>
                )}

                <button type="submit" disabled={loading} className="w-full py-3 bg-[#E52020] text-white font-bold rounded-full flex items-center justify-center gap-2 hover:bg-[#CC1B1B] hover:shadow-[0_8px_24px_rgba(229,32,32,0.34)] transition-all tracking-[0.14em] uppercase disabled:opacity-30" data-testid="login-submit-btn">
                  {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : (<>{mode === "register" ? "CREATE ACCOUNT" : "SIGN IN"}<ArrowRight className="w-4 h-4" /></>)}
                </button>
              </form>

              <p className="text-center text-sm text-[#666666] mt-5 font-['Manrope']">
                {mode === "register" ? "Already have an account?" : "Don't have an account?"}
                <button onClick={() => setMode(mode === "register" ? "login" : "register")} className="text-[#E52020] hover:underline ml-1.5 font-semibold" data-testid="toggle-auth-mode">
                  {mode === "register" ? "Sign in" : "Sign up"}
                </button>
              </p>

              <button onClick={() => onLogin(null)} className="w-full mt-4 py-2.5 text-sm text-[#666666] border border-[#EBE6DC] rounded-full hover:text-[#1A1A1A] hover:border-[#E52020] transition-all font-['Manrope']" data-testid="continue-guest-btn">
                Continue as guest
              </button>
            </>
          )}

          {/* Contact footer — always visible across all modes */}
          <div className="mt-8 pt-6 border-t border-[#EBE6DC] text-center">
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#999999] mb-2">Need help?</p>
            <a
              href="mailto:admin@drivespend.nz"
              className="inline-flex items-center gap-1.5 text-sm text-[#1A1A1A] hover:text-[#E52020] transition-colors font-['Manrope'] font-medium"
              data-testid="contact-admin-link"
            >
              <Mail className="w-3.5 h-3.5" />
              admin@drivespend.nz
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
